import React, { useRef, useState, useEffect } from 'react';
import { WagmiProvider } from 'wagmi';
import { RainbowKitProvider, darkTheme } from '@rainbow-me/rainbowkit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { OnchainKitProvider } from '@coinbase/onchainkit';
import { MiniKitProvider } from '@coinbase/onchainkit/minikit';
import { base } from 'wagmi/chains';
import { config } from './config/wagmi';
import Header from './components/Header';
import Hero from './components/Hero';
import ConfessionForm from './components/TestimonyForm';
import ConfessionGallery from './components/TestimonyGallery';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import CookieConsent from './components/CookieConsent';
import BasicAuthModal from './components/BasicAuthModal';
import MiniAppWrapper from './components/MiniAppWrapper';
import MiniAppBottomNav from './components/MiniAppBottomNav';
import { trackPageView } from './utils/analytics';

import '@rainbow-me/rainbowkit/styles.css';
import '@coinbase/onchainkit/styles.css';
import './App.css';

const queryClient = new QueryClient();

// Suppress Coinbase wallet console errors and development warnings
const suppressUnnecessaryLogs = () => {
  const originalError = console.error;
  console.error = (...args) => {
    const message = args[0]?.toString?.() || '';
    
    // Suppress specific development warnings and Coinbase errors
    if (message.includes('Cannot update a component') || 
        message.includes('ReactDevTools') ||
        message.includes('cca-lite.coinbase.com') ||
        message.includes('setState() call inside')) {
      return;
    }
    
    originalError.apply(console, args);
  };
  
  const originalWarn = console.warn;
  console.warn = (...args) => {
    const message = args[0]?.toString?.() || '';
    
    if (message.includes('ReactDevTools') ||
        message.includes('React DevTools') ||
        message.includes('cca-lite.coinbase.com') ||
        message.includes('Lit is in dev mode')) {
      return;
    }
    
    originalWarn.apply(console, args);
  };

  const originalLog = console.log;
  console.log = (...args) => {
    const message = args[0]?.toString?.() || '';
    
    if (message.includes('ReactDevTools') ||
        message.includes('React DevTools')) {
      return;
    }
    
    originalLog.apply(console, args);
  };
};

// Initialize log suppression
suppressUnnecessaryLogs();

function App() {
  const confessionGalleryRef = useRef();
  const [isFormFocused, setIsFormFocused] = useState(false);
  const [unlockedCount, setUnlockedCount] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Track initial page view and check authentication
  useEffect(() => {
    trackPageView(window.location.pathname + window.location.search, document.title);
    
    // Check for authentication
    const authEnabled = process.env.REACT_APP_ENABLE_AUTH === 'true';
    if (!authEnabled) {
      setIsAuthenticated(true);
      return;
    }
    
    // Check if already authenticated
    const authToken = sessionStorage.getItem('basicAuthToken');
    if (authToken) {
      setIsAuthenticated(true);
    }
  }, []);

  return (
    <OnchainKitProvider
      apiKey={process.env.REACT_APP_ONCHAINKIT_API_KEY}
      chain={base}
      config={{
        appearance: {
          mode: 'dark',
          theme: 'base',
        },
      }}
    >
      <MiniKitProvider>
        <WagmiProvider config={config}>
          <QueryClientProvider client={queryClient}>
            <RainbowKitProvider
              theme={darkTheme({
                accentColor: '#667eea',
                accentColorForeground: 'white',
                borderRadius: 'medium',
                fontStack: 'system',
                overlayBlur: 'small',
              })}
            >
            <MiniAppWrapper>
            {/* Basic Auth Modal */}
            {!isAuthenticated ? (
              <BasicAuthModal onAuthenticated={() => setIsAuthenticated(true)} />
            ) : (
              <div className={`App ${isFormFocused ? 'form-focused' : ''}`}>
            {/* Focus overlay */}
            {isFormFocused && <div className="focus-overlay" />}
            
            <div aria-hidden={isFormFocused}>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#1f2937',
                    color: '#f9fafb',
                    borderRadius: '12px',
                    border: '1px solid #374151',
                  },
                }}
              />
            </div>
            <div aria-hidden={isFormFocused}>
              <Header 
                confessionGalleryRef={confessionGalleryRef} 
                isHidden={isFormFocused}
                unlockedCount={unlockedCount}
              />
            </div>
            <main>
              <div 
                className={isFormFocused ? 'content-dimmed' : ''} 
                aria-hidden={isFormFocused}
              >
                <Hero />
              </div>
              <ConfessionForm onFocusChange={setIsFormFocused} />
              <div 
                className={isFormFocused ? 'content-dimmed' : ''} 
                aria-hidden={isFormFocused}
              >
                <ConfessionGallery 
                  ref={confessionGalleryRef} 
                  onUnlockedCountChange={setUnlockedCount}
                />
              </div>
            </main>
            <div aria-hidden={isFormFocused}>
              <Footer isHidden={isFormFocused} />
            </div>
            <div aria-hidden={isFormFocused}>
              <ScrollToTop isHidden={isFormFocused} />
            </div>
            
            {/* Cookie Consent Banner */}
            <CookieConsent />
            
            {/* MiniApp Bottom Navigation (only shows in miniapp mode) */}
            <MiniAppBottomNav 
              confessionGalleryRef={confessionGalleryRef} 
              unlockedCount={unlockedCount}
            />
          </div>
          )}
          </MiniAppWrapper>
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
      </MiniKitProvider>
    </OnchainKitProvider>
  );
}

export default App;